import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, CalendarHeart, Languages } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, t, to } = useLang();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const switchLang = (target) => {
    const path = location.pathname;
    const next =
      target === "es" ? (path === "/" ? "/es" : `/es${path}`) : path === "/es" ? "/" : path.replace(/^\/es/, "");
    navigate(next);
  };

  const LINKS = [
    { to: "/", label: t.nav.home, testid: "nav-link-home" },
    { to: "/services", label: t.nav.services, testid: "nav-link-services" },
    { to: "/podcast", label: t.nav.podcast, testid: "nav-link-podcast" },
    { to: "/about", label: t.nav.about, testid: "nav-link-about" },
    { to: "/testimonials", label: t.nav.testimonials, testid: "nav-link-testimonials" },
    { to: "/booking", label: t.nav.booking, testid: "nav-link-booking" },
  ];

  const LangToggle = ({ mobile }) => (
    <div
      data-testid={mobile ? "lang-toggle-mobile" : "lang-toggle"}
      className="inline-flex items-center rounded-full border border-border bg-white/70 p-0.5"
      role="group"
      aria-label="Language"
    >
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`lang-switch-${l}`}
          onClick={() => switchLang(l)}
          aria-pressed={lang === l}
          className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
            lang === l ? "bg-espresso text-linen" : "text-taupe hover:text-espresso"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,box-shadow] duration-300 ${
        scrolled || open ? "bg-linen/95 shadow-[0_1px_0_0_rgba(140,122,107,0.18)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-20 flex items-center justify-between">
        <Link to={to("/")} data-testid="nav-logo" className="flex items-baseline gap-2">
          <span className="font-serif text-2xl font-semibold tracking-tight text-[#6b7c7a]">Natalia</span>
          <span className="text-[11px] uppercase tracking-[0.22em] text-taupe font-semibold">Life Coach</span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={to(l.to)}
              data-testid={l.testid}
              className={({ isActive }) =>
                `text-sm font-medium whitespace-nowrap transition-colors duration-200 hover:text-terracotta ${
                  isActive ? "text-terracotta" : "text-clay"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden sm:inline-flex">
            <LangToggle />
          </span>
          <Link
            to={to("/booking")}
            data-testid="nav-book-discovery-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-terracotta text-linen px-5 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-200 hover:bg-terracotta-dark [@media(hover:hover)]:hover:-translate-y-0.5"
          >
            <CalendarHeart className="w-4 h-4" />
            {t.nav.bookCall}
          </Link>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="xl:hidden p-2 rounded-full text-espresso hover:bg-cream transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="xl:hidden bg-linen border-t border-border">
          <nav className="px-6 py-6 flex flex-col gap-1" aria-label="Mobile">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={to(l.to)}
                data-testid={`mobile-${l.testid}`}
                className={({ isActive }) =>
                  `py-3 text-lg font-serif border-b border-border/60 last:border-0 ${
                    isActive ? "text-terracotta" : "text-espresso"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-4 flex items-center gap-3">
              <Languages className="w-4 h-4 text-taupe" />
              <LangToggle mobile />
            </div>
            <Link
              to={to("/booking")}
              data-testid="mobile-nav-book-discovery-btn"
              className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-linen px-6 py-3.5 text-base font-semibold"
            >
              <CalendarHeart className="w-5 h-5" />
              {t.nav.bookCallFull}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
